import { useEffect, useRef } from "react";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";
import { getAuth, signInWithCustomToken } from "firebase/auth/web-extension";
import { sessionService } from "redux-react-session";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  btcToSatsAmount,
  callAPIInterface,
  generateAmountToPass,
  getFirebaseTokenForAccount,
  lightningLabel,
  assetMaxAmountForFPJSCheck,
} from "../components/constants";
import {
  failedSendErrorCodes,
  redemptionFailSubMessage,
} from "../components/messages";
import { app } from "../util/firebase";
import { showToast } from "../redux/actions";
import { useFPJSData } from "./useFPJSData";

function useSendPayment({
  amount,
  currency,
  note,
  data,
  accountId,
  appliedCoupon,
  selectedContact,
  withdrawDetails,
  isLnUrlMethodReceive,
  showBtcAmount,
  setErrorResponse,
  withdrawStatus,
  setOpenCaptchaModal,
  setButtonLoader,
  setPaymentFailedMsg,
  setRedemptionFail,
  setIsPaymentFailed,
  setIsShowErrorModal,
  setWithdrawDetails,
  setWithdrawStatus,
  setIsNodeRestricted,
  setIsSendModalOpen,
  setIsPaymentProcessing,
  setIsConfirmModalOpen,
  setIsOpen,
  setShowLoader,
  isFromMiniApp = false,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const unsubscribe = useRef();
  const timeoutId = useRef();
  const { setNewLocalstorageFPJSData } = useFPJSData();

  useEffect(() => {
    switch (withdrawStatus) {
      case "unpaid":
      case "pending":
        timeoutId.current = setTimeout(() => {
          unsubscribe.current?.();
          setIsPaymentProcessing(true);
          setIsShowErrorModal(true);
          setButtonLoader(false);
        }, 30000);
        break;
      case "paid":
        unsubscribe.current?.();
        if (isFromMiniApp) {
          setIsConfirmModalOpen?.(true);
          setIsOpen?.(false);
        } else {
          clearTimeout(timeoutId.current);
          navigate("/success-page", {
            state: {
              details: withdrawDetails,
              type: "withdraw",
              target_currency: currency,
              withdraw_request: !selectedContact?.payment_address_id
                ? data.withdraw_request
                : null,
            },
          });
        }
        setButtonLoader(false);
        break;
      case "failed":
        unsubscribe.current?.();
        handleFailedPayment(withdrawDetails);
        break;
      default:
        break;
    }

    return () => {
      clearTimeout(timeoutId.current);
    };
  }, [withdrawStatus]);

  const handleFailedPayment = (res) => {
    const reason =
      failedSendErrorCodes[res?.failure_reason] || res?.failure_reason;
    setPaymentFailedMsg(reason);
    setButtonLoader(false);
    setIsPaymentFailed(true);
    setIsShowErrorModal(true);
  };

  const handleAnomaly = async (isFromCaptcha) => {
    const now = Date.now();
    const currentSession = await sessionService.loadSession();
    let withdrawProcessed = currentSession?.withdraw_processed;
    let lastWithdrawProcessedTime =
      currentSession?.last_withdraw_processed_time;

    const setToInitial =
      !withdrawProcessed ||
      now - lastWithdrawProcessedTime > 60000 ||
      isFromCaptcha;

    if (setToInitial) {
      await sessionService.saveSession({
        ...currentSession,
        withdraw_processed: 1,
        last_withdraw_processed_time: now,
      });
    } else {
      await sessionService.saveSession({
        ...currentSession,
        withdraw_processed: withdrawProcessed + 1,
      });
    }
  };

  const getWithdrawData = async (withdrawId) => {
    const db = getFirestore(app);

    const withdrawRef = doc(db, "account", accountId, "withdraws", withdrawId);

    try {
      unsubscribe.current = onSnapshot(withdrawRef, async (querySnapshot) => {
        const data = querySnapshot.data();
        if (data) {
          // window.location.search && clearSearch();
          setWithdrawDetails(data);
          setWithdrawStatus(data?.status?.toLowerCase());
        }
      });
    } catch (err) {
      console.log(
        "Error while attaching listener to the Account firestore",
        err
      );
    }
  };

  const handleReceiveLnURL = async () => {
    try {
      const paymentData = {
        amount: generateAmountToPass(amount, currency),
        currency: currency,
        ttl: 600,
        statement_descriptor: note,
        target_currency: currency,
        payment_methods: ["lightning"],
      };
      setButtonLoader(true);
      const paymentRes = await callAPIInterface(
        "POST",
        "/payments",
        paymentData
      );
      if (paymentRes) {
        const url = `${data?.callback}?k1=${data?.k1}&pr=${paymentRes?.payment_method_options?.lightning?.payment_request}`;
        const receiveLnURLRes = await callAPIInterface(
          "GET",
          url,
          "",
          null,
          null,
          true
        );

        if (
          receiveLnURLRes?.status?.toLowerCase() === "error" &&
          receiveLnURLRes?.reason
        ) {
          setPaymentFailedMsg(receiveLnURLRes?.reason);
          setIsPaymentFailed(true);
          setIsShowErrorModal(true);
        } else if (receiveLnURLRes?.status?.toLowerCase() === "ok") {
          if (isFromMiniApp) {
            setIsConfirmModalOpen?.(true);
            setIsOpen?.(false);
          } else {
            navigate("/success-page", {
              state: {
                details: {
                  target_amount_paid: showBtcAmount
                    ? btcToSatsAmount(amount)
                    : amount,
                  target_currency: currency,
                  statement_descriptor: note,
                },
                type: "payment",
              },
            });
          }
        } else {
          setErrorResponse(receiveLnURLRes);
          setPaymentFailedMsg(redemptionFailSubMessage);
          setRedemptionFail(true);
          setIsShowErrorModal(true);
        }
      }
    } catch (err) {
      const error = err?.response?.data;
      if (error?.status?.toLowerCase() === "error" && error?.reason) {
        setPaymentFailedMsg(error?.reason);
        setIsPaymentFailed(true);
        setIsShowErrorModal(true);
      }
    } finally {
      // window.location.search && clearSearch();
      setButtonLoader(false);
    }
  };

  const handleSendPayment = async (isFromCaptcha = false) => {
    setShowLoader?.(false);
    if (isLnUrlMethodReceive) {
      handleReceiveLnURL?.();
      return;
    }

    let url = "/send";
    let withdrawData = {
      amount: generateAmountToPass(amount, currency),
      currency: currency,
      target_currency: currency,
      note: note || undefined,
      withdraw_request: data?.withdraw_request,
      withdraw_method:
        (data?.withdraw_method === "lnurl" && lightningLabel) ||
        data?.withdraw_method,
      promo_code: appliedCoupon?.name || undefined,
      contact_payment_address_id:
        selectedContact?.payment_address_id || undefined,
    };
    const shouldSetNewFPJSData =
      generateAmountToPass(amount, currency) >
      assetMaxAmountForFPJSCheck[currency];

    if (shouldSetNewFPJSData) {
      await setNewLocalstorageFPJSData({ linkedId: accountId });
    }

    setButtonLoader(true);
    callAPIInterface("POST", url, withdrawData)
      .then(async (res) => {
        handleAnomaly(isFromCaptcha);
        const instantStatus = ["paid", "failed"];
        if (instantStatus.includes(res?.status)) {
          if (res?.status === "paid") {
            const responseData = {
              target_currency: currency,
              target_amount_paid: res?.target_amount || res?.amount,
              statement_descriptor: res?.note || note,
            };
            navigate("/success-page", {
              state: {
                details: responseData,
                type: "withdraw",
                target_currency: currency,
                withdraw_request: !selectedContact?.payment_address_id
                  ? data.withdraw_request
                  : null,
              },
            });
            setButtonLoader(false);
          } else {
            handleFailedPayment(res);
          }
        } else {
          const token = await getFirebaseTokenForAccount();
          const authObj = getAuth(app);

          token?.firebase_token &&
            signInWithCustomToken(authObj, token.firebase_token)
              .then(() => {
                getWithdrawData(res?.withdraw_id);
              })
              .catch(() => {});
        }
      })
      .catch((err) => {
        dispatch(
          showToast({
            isToastOpen: false,
            toastVariant: "error",
          })
        );
        const errMsg = err?.response?.data?.errors?.[0]?.message || "";
        if (errMsg?.toString()?.startsWith("Speed has restricted")) {
          setIsNodeRestricted(true);
        }
        if (err?.response?.data?.errors?.[0]?.type === "account_restriction") {
          setIsSendModalOpen(false);
          return;
        }
        setButtonLoader(false);
        setIsPaymentFailed(true);
        setIsShowErrorModal(true);
        setPaymentFailedMsg(errMsg);
      });
  };

  const handleSubmit = async () => {
    const now = Date.now();
    const currentSession = await sessionService.loadSession();

    if (
      currentSession?.withdraw_processed >= 5 &&
      now - currentSession?.last_withdraw_processed_time < 60000
    ) {
      setOpenCaptchaModal?.(true);
      return;
    }
    handleSendPayment();
  };
  return { handleSubmit, handleSendPayment };
}

export default useSendPayment;
